/**
 * BlueprintAgentBanner.jsx
 * 
 * Banner component shown on Agent Details page when agent was
 * generated from the Blueprint Generator flow.
 * 
 * Shows:
 * - Context about blueprint generation
 * - Estimated ROI impact from the source opportunity
 * - Quick action CTAs
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  CurrencyDollar,
  Lightning,
  PencilSimple,
  Sparkle,
  TrendUp
} from '@phosphor-icons/react';

const BlueprintAgentBanner = ({ 
  opportunityData = null, // Optional ROI data from linked opportunity
  onCustomize = null 
}) => {
  // Extract ROI data if available
  const annualSavings = opportunityData?.roi?.annual_savings || 
                        opportunityData?.roi?.roi_annual || 
                        opportunityData?.estimated_annual_impact ||
                        null;
  
  const formattedSavings = annualSavings 
    ? `$${annualSavings.toLocaleString()}`
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-[#6366F1]/10 via-[#8B5CF6]/10 to-[#EC4899]/10 border border-[#6366F1]/30"
      data-testid="blueprint-agent-banner"
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center">
          <Sparkle className="text-white" size={24} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-white">
              Agent generated from your AI Blueprint
            </h3>
          </div>
          
          <p className="text-sm text-[#A0AEC0] mb-3">
            This agent was automatically designed based on your process analysis. 
            You can activate it immediately or customise it.
          </p>
          
          {/* ROI Impact (if available) */}
          {formattedSavings && (
            <div className="flex items-center gap-2 text-sm">
              <CurrencyDollar size={14} className="text-[#14F195]" />
              <span className="text-[#A0AEC0]">Estimated annual impact:</span>
              <span className="font-semibold text-[#14F195]">{formattedSavings}</span>
            </div>
          )}
        </div>
        
        {/* CTA (optional) */}
        {onCustomize && (
          <button
            onClick={onCustomize}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#6366F1] hover:text-white hover:bg-[#6366F1]/20 rounded-lg border border-[#6366F1]/30 transition-colors"
          >
            <PencilSimple size={14} />
            Customise
          </button>
        )}
      </div>
    </motion.div>
  );
};

/**
 * ROI Value Box - Shown near activation button
 * Reinforces value proposition for free agent hosting
 */
export const ROIValueBox = ({ annualSavings, className = '' }) => {
  if (!annualSavings) return null;
  
  const formattedSavings = `$${annualSavings.toLocaleString()}`;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`p-3 rounded-xl bg-[#14F195]/10 border border-[#14F195]/30 ${className}`}
      data-testid="roi-value-box"
    >
      <div className="flex items-center gap-2">
        <TrendUp size={16} className="text-[#14F195]" />
        <div>
          <p className="text-xs text-[#A0AEC0]">Estimated Annual Savings</p>
          <p className="text-lg font-bold text-[#14F195]">{formattedSavings}</p>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * AI Model Status Indicator
 * Shows whether org has LLM key configured
 */
export const AIModelStatusIndicator = ({ hasKey, provider = null, className = '' }) => {
  return (
    <div 
      className={`flex items-center gap-2 text-sm ${className}`}
      data-testid="ai-model-status"
    >
      {hasKey ? (
        <>
          <div className="w-2 h-2 rounded-full bg-[#14F195]" />
          <span className="text-[#14F195]">AI Model Connected</span>
          {provider && (
            <span className="text-gray-500">({provider})</span>
          )}
        </>
      ) : (
        <>
          <div className="w-2 h-2 rounded-full bg-amber-400" />
          <span className="text-amber-400">AI Model Not Connected</span>
        </>
      )}
    </div>
  );
};

export default BlueprintAgentBanner;
